/**
 * Per-chart target-vs-actual "traffic light" rating.
 *
 * Ports the legacy Delphi `Lampa` logic (old/delphi/uNewGrafice.pas): a chart's
 * actual value for a chosen period is divided by its saved target (the reference
 * line stored in the `averages` table) and the resulting ratio lights one of four
 * lamps. Thresholds are preserved exactly from the legacy source.
 */

export type TrafficLightLevel = 'red' | 'yellow' | 'green' | 'blue';

export interface TrafficLightRating {
    /** actual / target */
    ratio: number;
    /** (ratio - 1) * 100, i.e. percentage above/below target */
    percent: number;
    level: TrafficLightLevel;
}

export interface SeriesPoint {
    /** Period label as shown on the chart's x-axis (already timeframe-formatted). */
    label: string;
    value: number;
}

/** Upper bounds for each lamp: red <= 0.75, yellow <= 0.9, green <= 1.05, blue > 1.05. */
export const TRAFFIC_LIGHT_THRESHOLDS = {
    red: 0.75,
    yellow: 0.9,
    green: 1.05,
} as const;

/** Lamp order, top-to-bottom, matching the legacy panel layout. */
export const TRAFFIC_LIGHT_LEVELS: readonly TrafficLightLevel[] = ['red', 'yellow', 'green', 'blue'];

function resolveLevel(ratio: number): TrafficLightLevel {
    if (ratio <= TRAFFIC_LIGHT_THRESHOLDS.red) return 'red';
    if (ratio <= TRAFFIC_LIGHT_THRESHOLDS.yellow) return 'yellow';
    if (ratio <= TRAFFIC_LIGHT_THRESHOLDS.green) return 'green';
    return 'blue';
}

/**
 * Rate an actual value against a target. Returns null when no meaningful rating is
 * possible (non-finite inputs or a non-positive target, which would divide by zero).
 */
export function rateAgainstTarget(actual: number, target: number): TrafficLightRating | null {
    if (!Number.isFinite(actual) || !Number.isFinite(target) || target <= 0) {
        return null;
    }
    const ratio = actual / target;
    return { ratio, percent: (ratio - 1) * 100, level: resolveLevel(ratio) };
}

/** Metric keys used across the wrapped chart data shapes ({date,value|count|grams}). */
const METRIC_KEYS = ['value', 'count', 'grams', 'current'] as const;

function readMetric(record: Record<string, unknown>): number | null {
    for (const key of METRIC_KEYS) {
        const raw = record[key];
        if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    }
    return null;
}

function toSeriesPoint(item: unknown): SeriesPoint | null {
    if (typeof item !== 'object' || item === null) return null;
    const record = item as Record<string, unknown>;
    const metric = readMetric(record);
    if (metric === null) return null;
    const label = typeof record.date === 'string' ? record.date : '';
    return { label, value: metric };
}

/**
 * Normalise a chart's raw `data` prop into `{label, value}` points. Charts feed
 * heterogeneous shapes (value / count / grams); the first finite numeric metric
 * found on each row is used. Rows without a numeric metric are dropped.
 */
export function extractSeriesPoints(data: unknown): SeriesPoint[] {
    if (!Array.isArray(data)) return [];
    const points: SeriesPoint[] = [];
    for (const item of data) {
        const point = toSeriesPoint(item);
        if (point) points.push(point);
    }
    return points;
}

/**
 * Index of the period the traffic light should rate by default: the most recent
 * period with a non-zero value. Series often end in a run of zero buckets (e.g. the
 * `bilant`-backed daily revenue has a row for every calendar day, so quiet trailing
 * days read as 0); defaulting to the raw last period would always rate 0 against the
 * target (a useless -100%). Falls back to the last period when every value is zero,
 * and to -1 for an empty series (callers guard against empty before using it).
 */
export function defaultPeriodIndex(points: readonly SeriesPoint[]): number {
    for (let index = points.length - 1; index >= 0; index--) {
        if (points[index].value !== 0) return index;
    }
    return points.length - 1;
}

/** Format a percentage above/below target with an explicit sign, e.g. "+12.3%". */
export function formatTargetPercent(percent: number): string {
    const rounded = Math.round(percent * 10) / 10;
    const sign = rounded > 0 ? '+' : '';
    return `${sign}${rounded}%`;
}
